/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { IconEdit, IconPlus, IconMinus } from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import ToggleButton from "../../../components/ToggleButton";
import {
  useCategoryAllListData,
  useFetchItemCategoriesData,
  useItemCategoriesCreateMutation,
  useSUbCategoriesListData,
  useUpdateItemCategories,
  useUpdateItemCategoriesStatus,
} from "../../../apis/itemCategoriesApis";
import MessageModal from "../../../components/MessageModal";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import SelectInput from "../../../components/Select";
import MultiSelectInputNew from "../../../components/MultiSelectInputNew";
import formatDate from "../../../utilities/helper";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import { useLanguage } from "../../../context/LanguageContext";

type FormValues = {
  category: string;
  subCategory: string;
  fields: { label: string; value: string }[];
};

export default function ItemCategory() {
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [mode, setMode] = useState<"add" | "edit" | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [actionModal, setActionModal] = useState<boolean>(false);
  const [availableProductFields, setAvailableProductFields] = useState<any[]>(
    []
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const { t } = useLanguage();

  const {
    data: itemCategoryData,
    isLoading,
    isFetching,
    refetch,
  } = useFetchItemCategoriesData(pageNo, limit, "");

  useEffect(() => {
    refetch();
  }, [pageNo]);

  const { mutate: createCategory, isPending: createPending } =
    useItemCategoriesCreateMutation();
  const { mutate: updateCategory, isPending: updatePending } =
    useUpdateItemCategories();
  const { mutate: updateCategoryStatus, isPending: statusPending } =
    useUpdateItemCategoriesStatus();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      category: "",
      subCategory: "",
      fields: [],
    },
  });

  const { data: allCategories } = useCategoryAllListData();
  const { data: subCategories, refetch: fetchSubCategories } =
    useSUbCategoriesListData(
      allCategories?.find(
        (category: any) => category?.name === watch("category")
      )?.categoryId
    );

  const watchedCategory = watch("category");
  const watchedSubCategory = watch("subCategory");

  // Reset form when mode changes
  useEffect(() => {
    if (mode === "add") {
      reset({
        category: "",
        subCategory: "",
        fields: [],
      });
      setSelectedCategoryId("");
      setAvailableProductFields([]);
    }
  }, [mode, reset]);

  useEffect(() => {
    if (watchedSubCategory && subCategories) {
      const selectedSubCategory = subCategories.find(
        (sc: any) => sc.name === watchedSubCategory
      );

      if (
        selectedSubCategory?.fields &&
        Array.isArray(selectedSubCategory.fields)
      ) {
        const fieldsFromApi = selectedSubCategory.fields.map(
          (field: string) => ({
            label: field,
            value: field,
          })
        );
        setAvailableProductFields(fieldsFromApi);
        
        // Auto-select all available product fields when in add mode
        // OR when in edit mode and subcategory has changed from the original
        if (mode === "add") {
          setValue("fields", fieldsFromApi);
        } else if (mode === "edit" && selectedItem) {
          // In edit mode, check if the current subcategory is different from the original
          const originalSubCategory = selectedItem.subCategory;
          if (originalSubCategory !== watchedSubCategory) {
            // Subcategory changed, auto-select new fields
            setValue("fields", fieldsFromApi);
          }
          // If subcategory is the same as original, keep the existing field selection
        }
      } else {
        setAvailableProductFields([]);
        if (mode === "add") {
          setValue("fields", []);
        } else if (mode === "edit") {
          // In edit mode with no fields available, clear selection only if subcategory changed
          const originalSubCategory = selectedItem?.subCategory;
          if (originalSubCategory !== watchedSubCategory) {
            setValue("fields", []);
          }
        }
      }
    } else {
      setAvailableProductFields([]);
      if (mode === "add") {
        setValue("fields", []);
      } else if (mode === "edit") {
        // In edit mode with no subcategory selected, clear fields only if it was different
        if (!selectedItem?.subCategory) {
          setValue("fields", []);
        }
      }
    }
  }, [watchedSubCategory, subCategories, mode, setValue, selectedItem]);

  const handleAddClick = () => {
    if (mode === "edit") {
      setMode("add");
      setSelectedItem(null);
    } else {
      setMode(mode === "add" ? null : "add");
      setSelectedItem(null);
    }
  };

  useEffect(() => {
    if (watch("category")) {
      fetchSubCategories();
    }
  }, [watch("category")]);

  const handleEditClick = (item: any) => {
    setSelectedItem(item);
    setValue("category", item.category);
    setValue("subCategory", item?.subCategory);
    setSelectedCategoryId(
      allCategories?.find((category: any) => category?.name === item.category)
        ?.categoryId
    );

    if (item.fields && Array.isArray(item.fields)) {
      const fieldObjects = item?.fields?.map((field: string) => ({
        label: field,
        value: field,
      }));
      setValue("fields", fieldObjects);
    }
    setMode("edit");
    setTimeout(() => {
      // Primary: Try common dashboard content containers
      const selectors = [
        ".content",
        ".main-content",
        ".dashboard-content",
        "main",
        "[role='main']",
        ".overflow-y-auto",
        ".scrollable-content",
      ];

      let scrolled = false;
      for (const selector of selectors) {
        const el = document.querySelector(selector) as HTMLElement;
        if (el && el.scrollHeight > el.clientHeight) {
          el.scrollTo({ top: 0, behavior: "smooth" });
          scrolled = true;
          break;
        }
      }

      // Fallback: window
      if (!scrolled) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 100); // Increased timeout slightly for React re-render
  };

  const handleCancelEdit = () => {
    setMode(null);
    setSelectedItem(null);
    reset();
  };

  const handleToggleStatus = (item: any) => {
    setSelectedItem(item);
    setActionModal(true);
  };

  const confirmUpdateStatus = async () => {
    const newStatus = selectedItem?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    updateCategoryStatus(
      { id: selectedItem?.id, status: newStatus },
      {
        onSuccess: () => {
          refetch();
          toast.success(t("Status updated successfully"));
          setActionModal(false);
        },
        onError: (error) => toast.error(`${error}`),
      }
    );
  };

  const onSubmit = (data: FormValues) => {
    const submitData = {
      category: data.category,
      subCategory: data.subCategory,
      fields: data.fields.map((f) => f.value),
    };

    if (mode === "add") {
      createCategory(submitData, {
        onSuccess: () => {
          reset();
          refetch();
          setMode(null);
          toast.success(t("Category added successfully"));
        },
        onError: (error) => toast.error(`${error}`),
      });
    } else if (mode === "edit") {
      updateCategory(
        {
          id: selectedItem?.id,
          ...submitData,
        },
        {
          onSuccess: () => {
            reset();
            refetch();
            setMode(null);
            setSelectedItem(null);
            toast.success(t("Category updated successfully"));
          },
          onError: (error) => toast.error(`${error}`),
        }
      );
    }
  };

  const handlePaginationFetch = (page: number) => setPageNo(page);

  const getSubCategoryName = () => {
    if (!watchedSubCategory || !subCategories) return "";
    const subCat = subCategories.find(
      (sc: any) => sc.id === watchedSubCategory
    );
    return subCat?.name || "";
  };

  console.log(watch(), "w");

  return (
    <>
      <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] mb-10">
        <div className="flex justify-between items-center">
          <p className="text-lg font-medium text-[#212121]">{t("Item Category")}</p>
          <button
            onClick={handleAddClick}
            className="bg-[#FBD037] text-[#212121] text-sm font-semibold p-2 rounded-full"
          >
            {mode === "add" || mode === "edit" ? (
              <IconMinus stroke={1.5} size={20} />
            ) : (
              <IconPlus stroke={1.5} size={20} />
            )}
          </button>
        </div>

        {(mode === "add" || mode === "edit") && (
          <div className="bg-[#FCFDF8] p-4 rounded-lg border border-[#D2D2D2]">
            <h2 className="text-[#212121]/70 text-base font-medium mb-4">
              {mode === "add" ? t("Add Category") : t("Edit Category")}
            </h2>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-2 gap-4">
                <SelectInput
                  name="category"
                  label={t("Category")}
                  placeholder={t("Select Category")}
                  selectOptions={allCategories?.map((c: any) => ({
                    id: c.id,
                    value: c.name,
                    text: c.name,
                  }))}
                  register={register}
                  clearErrors={clearErrors}
                  validation={{ required: t("Category is required") }}
                  error={errors.category}
                  value={watch("category")}
                  setValue={(val: string) => setValue("category", val)}
                />

                <SelectInput
                  name="subCategory"
                  label={t("Sub Category")}
                  placeholder={
                    !watchedCategory
                      ? t("Select Category first")
                      : t("Select Sub Category")
                  }
                  selectOptions={
                    Array.isArray(subCategories)
                      ? subCategories.map((sc: any) => ({
                          id: sc.id,
                          value: sc.name,
                          text: sc.name,
                        }))
                      : []
                  }
                  register={register}
                  clearErrors={clearErrors}
                  validation={{ required: t("Sub Category is required") }}
                  error={errors.subCategory}
                  value={watch("subCategory")}
                  setValue={(val: string) => setValue("subCategory", val)}
                  disabled={!watchedCategory}
                />

                <div className="col-span-2">
                  <Controller
                    name="fields"
                    control={control}
                    rules={{ required: t("At least one field is required") }}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <MultiSelectInputNew
                        label={t("Select Product Fields")}
                        name="fields"
                        options={availableProductFields}
                        value={value || []}
                        onChange={onChange}
                        error={error}
                        disabled={!watchedSubCategory}
                        placeholder={
                          watchedSubCategory
                            ? `${t("Select fields for")} ${getSubCategoryName()}`
                            : t("Select Sub Category first")
                        }
                      />
                    )}
                  />
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="submit"
                  disabled={createPending || updatePending}
                  className={`bg-[#FBD037] text-[#212121] text-sm font-semibold px-4 py-2 rounded-full ${
                    createPending || updatePending
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {mode === "add" ? t("Save") : t("Update")}
                </button>

                {mode === "edit" && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="bg-[#000000] text-[#ffffff] text-sm font-semibold px-4 py-2 rounded-full"
                  >
                    {t("Cancel")}
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        <div className="-mx-4">
          {isLoading || isFetching ? (
            <TableSkeleton
              mainClass="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]"
              subClassName=""
              columns={[
                "#",
                t("Category"),
                t("Sub Category"),
                t("Required Fields"),
                t("Date"),
                t("Status"),
                t("Actions"),
              ]}
              rows={6}
            />
          ) : itemCategoryData?.itemCategories?.length ? (
            <ListView
              mainDivClassName="bg-transparent"
              tdClassName="rounded-r-none rounded-l-none"
              trClassName="hover:bg-[#FBD037]/20"
              mailDivClassName="p-0 bg-transparent"
              thClassName="border-t"
              data={itemCategoryData?.itemCategories || []}
              columns={[
                {
                  label: "#",
                  render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
                },
                { label: t("Category"), render: (c: any) => c.category },
                { label: t("Sub Category"), render: (c: any) => c.subCategory },
                {
                  label: t("Required Fields"),
                  render: (c: any) => (
                    <div className="p-2 rounded-md border-[0.5px] border-[#D2D2D2] min-h-[44px]">
                      {c.fields?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {c.fields.map((f: string, i: number) => (
                            <span
                              key={i}
                              className="bg-[#F7F6F2] p-1 px-2 text-xs border-[0.5px] rounded-full"
                            >
                              {f}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">
                          {t("No fields selected")}
                        </span>
                      )}
                    </div>
                  ),
                },
                {
                  label: t("Date"),
                  render: (c: any) => formatDate(c.createdAt),
                },
                {
                  label: t("Status"),
                  render: (c: any) => (
                    <div className="flex justify-center items-center">
                      <ToggleButton
                        onChange={() => handleToggleStatus(c)}
                        checked={c.status === "ACTIVE"}
                      />
                    </div>
                  ),
                },
                {
                  label: t("Actions"),
                  render: (c: any) => (
                    <div className="flex justify-center items-center">
                      <button
                        onClick={() => handleEditClick(c)}
                        className="p-2 border border-[#D2D2D2] rounded-full"
                      >
                        <IconEdit strokeWidth={1.5} size={20} />
                      </button>
                    </div>
                  ),
                },
              ]}
            />
          ) : (
            <NoDataFound
              subClassName=""
              mainClassName="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] rounded-b-3xl"
            />
          )}

          {itemCategoryData?.totalPages > 1 && (
            <div className="flex justify-center items-center pt-4">
              <Pagination
                currentPage={pageNo}
                totalPages={itemCategoryData?.totalPages}
                fetchData={handlePaginationFetch}
              />
            </div>
          )}
        </div>
      </div>

      <MessageModal
        open={actionModal}
        onClose={() => setActionModal(false)}
        title={t("Confirmation Required")}
        subtitle={t("Are you sure you want to perform this action?")}
        img={
          selectedItem?.status === "ACTIVE"
            ? "/img/inactive.svg"
            : "/img/active.svg"
        }
        showFooter={true}
        closeButtonText={t("Cancel")}
        actionButtonText={t("Confirm")}
        actionButtonOnClick={confirmUpdateStatus}
        isPending={statusPending}
        size="md"
      />
    </>
  );
}
